import { useRef, useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import UserMenu from '@/components/common/UserMenu'
import NotificationBell from '@/components/common/NotificationBell'
import ActiveTimerBadge from '@/components/common/ActiveTimerBadge'
import { SearchButton } from '@/components/common/SearchButton'
import { SearchDialog } from '@/components/common/SearchDialog'
import { MessageDialog } from '@/components/common/MessageDialog'
import useUserStore from '@/store/useUserStore'
import useNotificationStore from '@/store/useNotificationStore'
import { subscribeToLoading, getIsLoading } from '@/services/api'

function Navigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const { currentProjectId, isAuthenticated } = useUserStore()
  const { startPolling, stopPolling } = useNotificationStore()
  const noProjectDialogRef = useRef()
  const [isLoading, setIsLoading] = useState(getIsLoading)
  const authenticated = isAuthenticated()

  useEffect(() => {
    return subscribeToLoading(setIsLoading)
  }, [])

  // Start/stop notification polling based on authentication
  useEffect(() => {
    if (authenticated) {
      startPolling()
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [authenticated, startPolling, stopPolling])

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/projects'
    }
    if (path === '/my-tasks') {
      return location.pathname === '/my-tasks'
    }
    if (path === 'tasks') {
      return location.pathname.includes('/tasks') && !location.pathname.includes('/my-tasks')
    }
    if (path === 'board') {
      return location.pathname.includes('/board')
    }
    if (path === 'gantt') {
      return location.pathname.includes('/gantt')
    }
    if (path === 'dashboard') {
      return location.pathname.includes('/dashboard')
    }
    return location.pathname === path
  }

  const handleTaskListClick = (e) => {
    if (!currentProjectId) {
      e.preventDefault()
      noProjectDialogRef.current?.show({
        title: 'No Project Selected',
        message: 'Please select a project before viewing the task list.',
        okText: 'Go to Projects',
        onClose: () => navigate('/projects')
      })
    }
  }

  const handleTaskBoardClick = (e) => {
    if (!currentProjectId) {
      e.preventDefault()
      noProjectDialogRef.current?.show({
        title: 'No Project Selected',
        message: 'Please select a project before viewing the task board.',
        okText: 'Go to Projects',
        onClose: () => navigate('/projects')
      })
    }
  }

  const handleGanttClick = (e) => {
    if (!currentProjectId) {
      e.preventDefault()
      noProjectDialogRef.current?.show({
        title: 'No Project Selected',
        message: 'Please select a project before viewing the Gantt chart.',
        okText: 'Go to Projects',
        onClose: () => navigate('/projects')
      })
    }
  }

  const handleDashboardClick = (e) => {
    if (!currentProjectId) {
      e.preventDefault()
      noProjectDialogRef.current?.show({
        title: 'No Project Selected',
        message: 'Please select a project before viewing the dashboard.',
        okText: 'Go to Projects',
        onClose: () => navigate('/projects')
      })
    }
  }

  const dashboardPath = currentProjectId ? `/projects/${currentProjectId}/dashboard` : '/projects'
  const taskListPath = currentProjectId ? `/projects/${currentProjectId}/tasks` : '/projects'
  const taskBoardPath = currentProjectId ? `/projects/${currentProjectId}/board` : '/projects'
  const ganttPath = currentProjectId ? `/projects/${currentProjectId}/gantt` : '/projects'

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="flex h-16 items-center justify-center px-4">
          <div className="flex gap-2">
            <Button
              asChild
              variant={isActive('/') ? 'default' : 'ghost'}
              className={cn(isActive('/') && 'bg-primary text-primary-foreground')}
            >
              <Link to="/">Projects</Link>
            </Button>
            <Button
              asChild
              variant={isActive('/my-tasks') ? 'default' : 'ghost'}
              className={cn(isActive('/my-tasks') && 'bg-primary text-primary-foreground')}
              data-testid="my-tasks-nav-button"
            >
              <Link to="/my-tasks">My Tasks</Link>
            </Button>
            <Button
              asChild
              variant={isActive('dashboard') ? 'default' : 'ghost'}
              className={cn(isActive('dashboard') && 'bg-primary text-primary-foreground')}
              data-testid="dashboard-nav-button"
            >
              <Link to={dashboardPath} onClick={handleDashboardClick}>Dashboard</Link>
            </Button>
            <Button
              asChild
              variant={isActive('tasks') ? 'default' : 'ghost'}
              className={cn(isActive('tasks') && 'bg-primary text-primary-foreground')}
            >
              <Link to={taskListPath} onClick={handleTaskListClick}>Task List</Link>
            </Button>
            <Button
              asChild
              variant={isActive('board') ? 'default' : 'ghost'}
              className={cn(isActive('board') && 'bg-primary text-primary-foreground')}
            >
              <Link to={taskBoardPath} onClick={handleTaskBoardClick}>Task Board</Link>
            </Button>
            <Button
              asChild
              variant={isActive('gantt') ? 'default' : 'ghost'}
              className={cn(isActive('gantt') && 'bg-primary text-primary-foreground')}
              data-testid="gantt-nav-button"
            >
              <Link to={ganttPath} onClick={handleGanttClick}>Gantt</Link>
            </Button>
          </div>
          <div className="absolute right-4 flex items-center gap-2">
            {isLoading && (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" data-testid="api-loading-spinner" />
            )}
            {authenticated && (
              <>
                <ActiveTimerBadge />
                <SearchButton />
                <NotificationBell />
                <UserMenu />
                <SearchDialog />
              </>
            )}
          </div>
        </div>
      </nav>
      <MessageDialog ref={noProjectDialogRef} variant="default" />
    </>
  )
}

export default Navigation
